import React, { useContext, useState, useEffect, useCallback } from "react";
import { Save, RotateCcw, LocateFixed, Minus, Plus, Sparkles, Kanban, List, ChevronRight, LayoutList } from "lucide-react";
import { Button } from "@/modern/components/ui/button";
import { motion, AnimatePresence } from "motion/react";
import { ButtonGroup, ButtonGroupText } from "@/modern/components/ui/button-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/modern/components/ui/hover-card";
import BryntumContext, { userPreferences } from "../../context/bryntumContext.jsx";
import useViews from "../../hooks/useViews.jsx";
import { VIEW_TYPES } from "../../../other/constants/OtherKeys.jsx";
import { canSaveView, resetSubGrids } from "../../../other/utils/utils.jsx";
import { MessageDialog, StringHelper } from "@bryntum/schedulerpro";
import ViewStateBadges from "./ViewStateBadges.jsx";
import ViewMoreMenu from "./ViewMoreMenu.jsx";
import { GRANTS } from "../../../other/constants/PrivilegeTypes.jsx";
import { PERSISTENCE_STATE_KEYS } from "../../../other/constants/PersistenceStateKeys.jsx";
import { getScheduleInstanceFromViewManager } from "../../../other/utils/otherUtils.jsx";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { AppSearchBar } from "./AppSearchBar";
import { SwivuSolChatBox } from "@/modern/components/chatbox";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { cn } from "@/modern/utils";
import { TIMESCALES } from "@/modern/components/editors/viewEditor/tabs/TimescalesTab";
import { guideTargetProps, KANBAN_CARD_FIELDS_GUIDE_TARGETS, useAutoPageGuide } from "@/react/guides";

const formatTimelineZoomLabel = (scheduler) => {
  if (!scheduler?.tickSize) return "";
  const px = Math.round(scheduler.tickSize);
  const presetId = (scheduler.viewPreset?.id ?? "").split("-")[0];
  const timescale = TIMESCALES.find((t) => t.id === presetId);
  if (!timescale) return `${px}px`;
  const unit = timescale.name === "1/2 Hours" ? "½ hour" : timescale.name.toLowerCase().replace(/s$/, "");
  return `${px}px / ${unit}`;
};

const ViewSpecificHeader = ({ viewToggleButtonConfig }) => {
  const { viewManager, user, selectedBoard, currentViewRef } = useContext(BryntumContext);
  const viewManagerState = useViews();
  useSwivuStore({ storeId: STORE_KEYS.userPreferencesStore, subscriberFn: ({ record: changedRecord }) => changedRecord?.key === PERSISTENCE_STATE_KEYS.viewStatePIITS });
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatSessionId, setChatSessionId] = useState();
  const [chatBoardId, setChatBoardId] = useState();
  const [chatBoardViewId, setChatBoardViewId] = useState();
  const [isChatFullScreen, setIsChatFullScreen] = useState(false);
  const [timelineZoomLabel, setTimelineZoomLabel] = useState("");
  const [currentTickSize, setCurrentTickSize] = useState(null);

  const selectedView = viewManagerState?.selectedView;
  const isSchedulerView = selectedView && [VIEW_TYPES.schedulerView, VIEW_TYPES.planView].includes(selectedView.viewType);
  const kanbanGuideViewTypes = [VIEW_TYPES.tasks, VIEW_TYPES.resources, VIEW_TYPES.clients];
  const kanbanGuideEnabled =
    Boolean(viewToggleButtonConfig?.isKanbanMode) &&
    kanbanGuideViewTypes.includes(selectedView?.viewType);

  useAutoPageGuide({
    viewType: selectedView?.viewType ?? VIEW_TYPES.tasks,
    enabled: kanbanGuideEnabled,
    contextKey: viewToggleButtonConfig?.isKanbanMode ? "kanban" : "grid",
  });

  const syncTimelineZoomLabel = useCallback((scheduler) => {
    setTimelineZoomLabel(formatTimelineZoomLabel(scheduler));
    setCurrentTickSize(scheduler.tickSize);
  }, []);

  useEffect(() => {
    if (!isSchedulerView || !viewManager) return;

    let detacher = null;
    let retryTimer = null;

    const attach = () => {
      const scheduler = getScheduleInstanceFromViewManager(viewManager);
      if (!scheduler) return false;

      const syncHandler = () => syncTimelineZoomLabel(scheduler);
      syncHandler();
      detacher = scheduler.on({ tickSizeChange: syncHandler, presetChange: syncHandler });
      return true;
    };

    if (!attach()) {
      retryTimer = setInterval(() => {
        if (attach()) clearInterval(retryTimer);
      }, 200);
    }

    return () => {
      if (retryTimer) clearInterval(retryTimer);
      detacher?.();
    };
  }, [viewManager, isSchedulerView, selectedView?.id, syncTimelineZoomLabel]);

  // Update board and view IDs when they change (while chat is open)
  useEffect(() => {
    if (isChatOpen) {
      // Update board ID
      const board = getSavedBoardSelection();
      if (board?.id) {
        setChatBoardId(board.id);
      }
      // Update view ID
      if (viewManagerState?.selectedView?.id) {
        setChatBoardViewId(viewManagerState.selectedView.id);
      }
    }
  }, [isChatOpen, selectedBoard?.id, viewManagerState?.selectedView?.id]);

  if (!selectedView || !user || !selectedBoard) {
    return null;
  }

  const isButtonReadOnly = !user.can(GRANTS.grant_four);
  const canZoomOut = currentTickSize != null && currentTickSize > 50;
  const canZoomIn = currentTickSize != null && currentTickSize < 800;
  const canShowSaveButton = selectedView.isOwnView;
  const isSaveButtonDisabled = !viewManagerState.selectedView.hasPIITS; /* Only enable button if the view has PIITS */

  const handleSaveView = () => {
    if (canSaveView(selectedView.viewType)) {
      const isViewUpdated = viewManager.updateView(selectedView.id, selectedView);
      if (isViewUpdated) {
        MessageDialog.alert({
          title: "Success",
          message: "View updated successfully."
        });
      } else {
        MessageDialog.alert({
          title: "Failed",
          message: "Unable to update the view."
        });
      }
    }
  };

  const onMoveTimelineToCurrentDate = () => {
    const reDistributePrefSetting = StringHelper.safeJsonParse(userPreferences.getSetting(PERSISTENCE_STATE_KEYS.redistributeSubGridPanels, true));
    if (reDistributePrefSetting && viewManager.pageViewsRef) {
      resetSubGrids(Object.values(viewManager.pageViewsRef));
    }
    currentViewRef.current.scrollSchedulerTimeAxisToCurrDate();
  };

  const onTickSizeHandler = (value) => {
    const schedulerView = getScheduleInstanceFromViewManager(viewManager);
    if (schedulerView) {
      const tickSizeLimit = { min: 50, max: 800 };
      const tickSize = schedulerView?.tickSize + value;
      if (tickSize < tickSizeLimit.min) {
        schedulerView.tickSize = tickSizeLimit.min;
      } else if (tickSize > tickSizeLimit.max) {
        schedulerView.tickSize = tickSizeLimit.max;
      } else if (tickSize >= tickSizeLimit.min && tickSize <= tickSizeLimit.max) {
        schedulerView.tickSize = tickSize;
        if (!currentViewRef.current.swivuLastViewportCenterDate) {
          currentViewRef.current.swivuLastViewportCenterDate = schedulerView.viewportCenterDate;
        }
        if (currentViewRef.current.swivuLastViewportCenterDate) {
          schedulerView.scrollToDate(currentViewRef.current.swivuLastViewportCenterDate, { block: "center" });
        }
      }
    }
  };

  const handleTidyUpColumns = () => {
    if (viewManager.pageViewsRef) {
      resetSubGrids(Object.values(viewManager.pageViewsRef));
    }
  };

  const handleOpenChat = () => {
    // Get current board and view context when opening chat
    try {
      const board = getSavedBoardSelection();
      if (board?.id) {
        setChatBoardId(board.id);
      }
      if (viewManagerState?.selectedView?.id) {
        setChatBoardViewId(viewManagerState.selectedView.id);
      }
    } catch (error) {
      console.error("Failed to get board/view context:", error);
    }
    setIsChatOpen(true);
  };

  return (
    <div className="flex justify-between items-center min-h-12 px-5 bg-accent border-0 border-b border-b-border border-solid gap-4 text-foreground">
      {/* Left Side: Search and state badges */}
      <div className="flex items-center gap-4 text-foreground">
        <div className="w-fit">
          <AppSearchBar isKanbanMode={viewToggleButtonConfig?.isKanbanMode ?? false} />
        </div>
        <ViewStateBadges />
      </div>

      {/* Right Side: controls and actions */}
      <div className="flex items-center justify-end gap-2">
        {isSchedulerView && (
          <ButtonGroup className="mr-[6rem]">
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon-sm"
                  disabled={isButtonReadOnly || !canZoomOut}
                  onClick={() => onTickSizeHandler(-50)}
                  aria-label="Decrease column width"
                >
                  <Minus className="size-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Narrower columns</TooltipContent>
            </Tooltip>
            <ButtonGroupText className="h-8 min-w-[5.5rem] justify-center !rounded-none !border-y !border-r !border-l-0 !border-input !bg-background px-2 py-0 text-xs font-normal text-foreground shadow-none tabular-nums">
              {timelineZoomLabel || "—"}
            </ButtonGroupText>
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon-sm"
                  disabled={isButtonReadOnly || !canZoomIn}
                  onClick={() => onTickSizeHandler(50)}
                  aria-label="Increase column width"
                >
                  <Plus className="size-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Wider columns</TooltipContent>
            </Tooltip>
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5 px-3 text-xs"
                  disabled={isButtonReadOnly}
                  onClick={onMoveTimelineToCurrentDate}
                >
                  <LocateFixed className="size-3.5" />
                  Today
                </Button>
              </TooltipTrigger>
              <TooltipContent>Recenter timeline on today</TooltipContent>
            </Tooltip>
          </ButtonGroup>
        )}
        {viewToggleButtonConfig && (
          <>
            {viewToggleButtonConfig.openCardFieldsEditor && viewToggleButtonConfig.isKanbanMode && (
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon-sm"
                    onClick={() => viewToggleButtonConfig.openCardFieldsEditor()}
                    aria-label="Card fields"
                    {...guideTargetProps(KANBAN_CARD_FIELDS_GUIDE_TARGETS.openButton)}
                  >
                    <LayoutList />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Card fields</TooltipContent>
              </Tooltip>
            )}
            <ButtonGroup>
              <HoverCard openDelay={600} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <span className="inline-flex">
                    <Tooltip delayDuration={300}>
                      <TooltipTrigger asChild>
                        <Button
                          variant={viewToggleButtonConfig.isKanbanMode ? "default" : "outline"}
                          size="icon-sm"
                          className={cn(
                            "rounded-r-none",
                            !viewToggleButtonConfig.isGroupingApplied && !viewToggleButtonConfig.isKanbanMode && "opacity-50"
                          )}
                          onClick={() => {
                            // Only handle direct click to open kanban if grouping is applied
                            if (viewToggleButtonConfig.isGroupingApplied || viewToggleButtonConfig.isKanbanMode) {
                              viewToggleButtonConfig.handleOnToggleView("kanban");
                            }
                          }}
                        >
                          <Kanban />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {viewToggleButtonConfig.isGroupingApplied || viewToggleButtonConfig.isKanbanMode
                          ? "Kanban View"
                          : "Select grouping to enable Kanban"}
                      </TooltipContent>
                    </Tooltip>
                  </span>
                </HoverCardTrigger>
                <HoverCardContent
                  side="bottom"
                  align="start"
                  className="w-auto p-0 overflow-hidden"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                >
                  <div className="space-y-0">
                    <div className="px-4 py-2.5 bg-accent/30 border-b border-border">
                      <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground/80 mb-0.5">
                        Kanban Grouping
                      </div>
                      <div className="text-xs text-muted-foreground/60">
                        Choose field & sort order
                      </div>
                    </div>
                    <div className="p-1">
                    {viewToggleButtonConfig.kanbanFieldConfig && viewToggleButtonConfig.kanbanFieldConfig.length > 0 ? (
                      viewToggleButtonConfig.kanbanFieldConfig.map((config) => {
                        const fieldLabels = {
                          statusValue: "Status",
                          categoryValue: "Category",
                          phaseValue: "Phase",
                          teamValue: "Team",
                          tagsValue: "Tags",
                          typeValue: "Type"
                        };
                        const label = fieldLabels[config.grouperField] || config.grouperField;
                        const isActive = viewToggleButtonConfig.activeConfig?.grouperField === config.grouperField;
                        const hasCustomOrder = !!config.customOrderConfig;

                        const activeGroupOrder = isActive ? viewToggleButtonConfig.getActiveGroupOrder?.(config.grouperField) : null;

                        return (
                          <HoverCard key={config.grouperField} openDelay={200} closeDelay={100}>
                            <HoverCardTrigger asChild>
                              <button
                                className={cn(
                                  "w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center justify-between min-w-[160px]",
                                  "hover:bg-accent hover:text-accent-foreground",
                                  isActive && viewToggleButtonConfig.isKanbanMode && "bg-accent text-accent-foreground font-medium"
                                )}
                              >
                                <span className="flex items-center gap-2">
                                  {label}
                                  {isActive && viewToggleButtonConfig.isKanbanMode && <span className="text-xs">✓</span>}
                                </span>
                                <ChevronRight className="h-4 w-4" />
                              </button>
                            </HoverCardTrigger>
                            <HoverCardContent
                              side="right"
                              align="start"
                              className="w-48 p-1"
                              onOpenAutoFocus={(e) => e.preventDefault()}
                            >
                              <button
                                className={cn(
                                  "w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center justify-between",
                                  "hover:bg-accent hover:text-accent-foreground",
                                  activeGroupOrder === "asc" && viewToggleButtonConfig.isKanbanMode && "bg-accent text-accent-foreground font-medium"
                                )}
                                onClick={() => {
                                  viewToggleButtonConfig.applyGroupingAndOpenKanban(config.grouperField, "asc");
                                }}
                              >
                                <span>Group Asc</span>
                                {activeGroupOrder === "asc" && viewToggleButtonConfig.isKanbanMode && <span className="text-xs">✓</span>}
                              </button>
                              <button
                                className={cn(
                                  "w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center justify-between",
                                  "hover:bg-accent hover:text-accent-foreground",
                                  activeGroupOrder === "desc" && viewToggleButtonConfig.isKanbanMode && "bg-accent text-accent-foreground font-medium"
                                )}
                                onClick={() => {
                                  viewToggleButtonConfig.applyGroupingAndOpenKanban(config.grouperField, "desc");
                                }}
                              >
                                <span>Group Desc</span>
                                {activeGroupOrder === "desc" && viewToggleButtonConfig.isKanbanMode && <span className="text-xs">✓</span>}
                              </button>
                              {hasCustomOrder && (
                                <button
                                  className={cn(
                                    "w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center justify-between",
                                    "hover:bg-accent hover:text-accent-foreground",
                                    activeGroupOrder === "custom" && viewToggleButtonConfig.isKanbanMode && "bg-accent text-accent-foreground font-medium"
                                  )}
                                  onClick={() => {
                                    viewToggleButtonConfig.applyGroupingAndOpenKanban(config.grouperField, "custom");
                                  }}
                                >
                                  <span>Group By Settings</span>
                                  {activeGroupOrder === "custom" && viewToggleButtonConfig.isKanbanMode && <span className="text-xs">✓</span>}
                                </button>
                              )}
                            </HoverCardContent>
                          </HoverCard>
                        );
                      })
                      ) : (
                        <div className="px-3 py-2 text-xs text-muted-foreground">
                          No kanban fields available
                        </div>
                      )}
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>

              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <Button variant={!viewToggleButtonConfig.isKanbanMode ? "default" : "outline"} size="icon-sm" onClick={() => viewToggleButtonConfig.handleOnToggleView("grid")}>
                    <List />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Grid View</TooltipContent>
              </Tooltip>
            </ButtonGroup>
          </>
        )}
        <ButtonGroup>
            {/* Reset/Tidy Up Columns Button */}
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon-sm" onClick={handleTidyUpColumns} disabled={isButtonReadOnly}>
                  <RotateCcw />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Reset Columns</TooltipContent>
            </Tooltip>

            {/* Save View Button - Icon only, always visible */}
            {canShowSaveButton && (
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon-sm" onClick={handleSaveView} disabled={isSaveButtonDisabled}>
                    <Save />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Save View</TooltipContent>
              </Tooltip>
            )}

            {/* Swivu Sol AI Assistant Button */}
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon-sm" onClick={handleOpenChat}>
                  <Sparkles className="text-primary" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <div className="flex flex-col gap-1">
                  <span>Ask Swivu Sol AI</span>
                  <span className="text-[10px] text-amber-500">⚠️ Experimental Feature</span>
                </div>
              </TooltipContent>
            </Tooltip>

            {/* More Menu */}
            <ViewMoreMenu />
        </ButtonGroup>
      </div>

      {/* Floating Chat Window */}
      <AnimatePresence>
        {isChatOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className={
              isChatFullScreen
                ? "fixed inset-4 z-[9998] shadow-2xl rounded-lg overflow-hidden"
                : "fixed bottom-4 right-4 z-[9998] w-[500px] h-[650px] max-h-[calc(100vh-2rem)] shadow-2xl rounded-lg overflow-hidden"
            }
          >
            <div className="relative h-full">
              {/* Header buttons */}
              <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
                {/* Full screen toggle */}
                <button
                  onClick={() => setIsChatFullScreen(!isChatFullScreen)}
                  className="size-8 flex items-center justify-center rounded-full bg-background/80 hover:bg-background backdrop-blur-sm border border-border shadow-sm transition-colors"
                  aria-label={isChatFullScreen ? "Exit full screen" : "Full screen"}
                  title={isChatFullScreen ? "Exit full screen" : "Full screen"}
                >
                  <i className={`text-sm text-foreground ${isChatFullScreen ? "fa-solid fa-compress" : "fa-solid fa-expand"}`} />
                </button>
                {/* Close button */}
                <button
                  onClick={() => {
                    setIsChatOpen(false);
                    setIsChatFullScreen(false);
                  }}
                  className="size-8 flex items-center justify-center rounded-full bg-background/80 hover:bg-background backdrop-blur-sm border border-border shadow-sm transition-colors"
                  aria-label="Close chat"
                >
                  <i className="fa-solid fa-times text-sm text-foreground" />
                </button>
              </div>
              <SwivuSolChatBox
                welcomeMessage="Hello! I'm Swivu Sol, your AI assistant. I can help you with tasks, scheduling, projects, and more!&#10;&#10;⚠️ **Experimental Feature Notice**&#10;&#10;This AI assistant is currently in active development and testing. You may experience:&#10;- Slower response times than expected&#10;- Occasional inaccurate or incomplete information&#10; or Unexpected behavior&#10;&#10;We're working hard to improve the experience. Your feedback helps us make Swivu Sol better!"
                boardId={chatBoardId}
                boardViewId={chatBoardViewId}
                sessionId={chatSessionId}
                onSessionChange={setChatSessionId}
                currentUser={user}
                className="h-full"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ViewSpecificHeader;
